import React from 'react';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { GetInstallmentRefundRequestedEventPropsFragment } from '#types';
import { toSimpleLink } from '../../../lib/helpers';
import rpcShared from '@rockpapercoin/rpc-shared';
import { FlattenedUser } from '../../../types/user';

export const getInstallmentRefundRequestedEventPropsFragment = gql`
	fragment getInstallmentRefundRequestedEventProps on Event {
		id
		contact {
			id
			firstName
			lastName
			customer {
				id
				email
				clientUser {
					id
					assignedPlanner {
						id
						organization {
							id
							users {
								id
								role
							}
						}
					}
				}
			}
		}
		clientUser {
			id
		}
		vendor {
			id
			name
		}
		invoice {
			id
			title
		}
		actingUser {
			id
			orgUser {
				id
				firstName
				lastName
			}
		}
	}
`;

export const getInstallmentRefundRequestedEventProps = (
	eventProps: {
		event: GetInstallmentRefundRequestedEventPropsFragment;
		message: React.JSX.Element;
		links: {
			type: string;
			text: string;
			link: any;
			onClick?: ( () => any ) | null;
		}[];
		icon: string;
		className: string;
	},
	user: FlattenedUser
) => {
	const { event } = eventProps;

	if ( !event.invoice || !event.contact || !event.vendor ) {
		throw new Error( `Event ID ${ event.id } missing required data` );
	}

	const { invoice, contact, vendor, actingUser } = event;

	const viewerIsCustomer = user?.isLoggedIn
		? rpcShared.contacts.isCustomer( contact, user.user.id )
		: false;

	const viewerIsActingUser =
		user?.isLoggedIn && !!actingUser ? actingUser.id === user.user.id : false;

	const viewerIsAssignedPlannerOrAdmin = user?.isLoggedIn
		? rpcShared.contacts.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
			contact,
			user
		  )
		: false;

	const vendorLink = toSimpleLink( {
		text: vendor.name,
		link: {
			href: `/org/${ vendor.id }`,
		},
	} );

	const invoiceLinkProps = {
		text: invoice.title,
		link: {
			href: `/invoice/${ invoice.id }`,
		},
	};
	const invoiceLink = toSimpleLink( invoiceLinkProps );

	const customerName = rpcShared.contacts.getCustomerName( contact ).fullName;
	const customerLink = toSimpleLink( {
		text: customerName,
		link: {
			href: `/client/${ contact.customer.id }`,
		},
	} );

	const actingUserName = actingUser?.orgUser
		? `${ actingUser.orgUser.firstName } ${ actingUser.orgUser.lastName }`
		: undefined;

	const defaultLinks = [
		{
			type: 'secondary',
			text: 'View',
			link: invoiceLinkProps.link,
		},
	];

	if ( viewerIsCustomer ) {
		return {
			...eventProps,
			icon: 'dollar',
			links: defaultLinks,
			message: (
				<Typography>
					Your refund from { vendorLink } for { invoiceLink } is being processed.
				</Typography>
			),
		};
	}

	if ( viewerIsActingUser ) {
		return {
			...eventProps,
			icon: 'dollar',
			links: defaultLinks,
			message: (
				<Typography>
					You initiated { customerLink }'s refund for { invoiceLink }.
				</Typography>
			),
		};
	}

	const customerDisplay = viewerIsAssignedPlannerOrAdmin
		? customerLink
		: customerName;

	return {
		...eventProps,
		icon: 'dollar',
		links: defaultLinks,
		message: (
			<Typography>
				{ actingUserName } initiated a refund on { customerDisplay }'s invoice,{ ' ' }
				{ invoiceLink }.
			</Typography>
		),
	};
};
